/**
 * Comparing several shots of one moment.
 *
 * The default is to flip between them *in the same position and at the same
 * scale*, rather than to lay them out side by side. That is not a stylistic
 * preference — it is how the difference actually becomes visible. Two group
 * selfies shown next to each other are two small pictures your eye has to
 * travel between; the same two swapped in place make a blink, a turned head or
 * a hand in front of a face jump out immediately, because everything that did
 * not change stays perfectly still.
 *
 * Side by side is offered too, because it is the better answer for judging
 * framing rather than faces, and for a pair rather than a burst.
 */

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSlideshowMedia } from "../slideshow/useSlideshowMedia.js";
import type { RelatedSet } from "./phash.js";
import "./CompareView.css";

export interface CompareViewProps {
  groupId: string;
  set: RelatedSet;
  marked: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onClose: () => void;
}

type Mode = "flip" | "side";

export function CompareView({
  groupId,
  set,
  marked,
  onToggle,
  onClose,
}: CompareViewProps) {
  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState<Mode>("flip");

  // The same window loader the slideshow uses: neighbours stay decrypted, so
  // flipping is instant rather than a fetch each time.
  const media = useSlideshowMedia(groupId, set.ids, index);

  const step = useCallback(
    (delta: number) => {
      setIndex(
        (current) => (current + delta + set.ids.length) % set.ids.length,
      );
    },
    [set.ids.length],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowRight":
          event.preventDefault();
          step(1);
          break;
        case "ArrowLeft":
          event.preventDefault();
          step(-1);
          break;
        case " ": {
          // Space toggles the one you are looking at, which is the whole
          // decision this screen exists to support.
          event.preventDefault();
          const current = set.ids[index];
          if (current) onToggle(current);
          break;
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, onClose, onToggle, set.ids, index]);

  const currentId = set.ids[index];

  return createPortal(
    <div
      className="fc-compare"
      role="dialog"
      aria-modal="true"
      aria-label="Compare photos"
    >
      <header className="fc-compare__bar">
        <button
          type="button"
          className="fc-compare__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div
          className="fc-compare__modes"
          role="radiogroup"
          aria-label="How to compare"
        >
          {(["flip", "side"] as Mode[]).map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={mode === option}
              data-active={mode === option || undefined}
              onClick={() => setMode(option)}
            >
              {option === "flip" ? "Flip" : "Side by side"}
            </button>
          ))}
        </div>

        <span className="fc-compare__count fc-tabular">
          {mode === "flip"
            ? `${index + 1} / ${set.ids.length}`
            : `${set.ids.length} photos`}
        </span>
      </header>

      {mode === "flip" ? (
        <div className="fc-compare__stage">
          {/*
            Every frame is rendered and stacked; only one is visible. Swapping
            opacity keeps them in identical positions, which is the point —
            re-laying out on each change would reintroduce exactly the jitter
            that hides the difference.
          */}
          {set.ids.map((id, position) => {
            const url = media.urls.get(id);
            return url ? (
              <img
                key={id}
                src={url}
                alt=""
                className="fc-compare__frame"
                data-current={position === index || undefined}
                data-marked={marked.has(id) || undefined}
              />
            ) : null;
          })}
          {!media.urls.has(currentId ?? "") && (
            <p className="fc-compare__waiting">Loading…</p>
          )}
        </div>
      ) : (
        <div
          className="fc-compare__grid"
          data-count={Math.min(set.ids.length, 4)}
        >
          {set.ids.map((id) => {
            const url = media.urls.get(id);
            return (
              <button
                key={id}
                type="button"
                className="fc-compare__cell"
                data-marked={marked.has(id) || undefined}
                aria-pressed={marked.has(id)}
                onClick={() => onToggle(id)}
              >
                {url ? (
                  <img src={url} alt="" />
                ) : (
                  <span className="fc-compare__blank" />
                )}
              </button>
            );
          })}
        </div>
      )}

      <footer className="fc-compare__foot">
        {mode === "flip" && (
          <button
            type="button"
            className="fc-compare__decide"
            data-marked={
              currentId !== undefined && marked.has(currentId) ? "" : undefined
            }
            onClick={() => currentId && onToggle(currentId)}
          >
            {currentId !== undefined && marked.has(currentId)
              ? "To trash"
              : "Keeping"}
          </button>
        )}

        <div className="fc-compare__strip">
          {set.ids.map((id, position) => {
            const url = media.urls.get(id);
            return (
              <button
                key={id}
                type="button"
                className="fc-compare__thumb"
                data-current={position === index || undefined}
                data-marked={marked.has(id) || undefined}
                aria-label={`Show photo ${position + 1}`}
                onClick={() => setIndex(position)}
              >
                {url ? (
                  <img src={url} alt="" />
                ) : (
                  <span className="fc-compare__blank" />
                )}
              </button>
            );
          })}
        </div>
      </footer>
    </div>,
    document.body,
  );
}
